import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';

import css from './InstallmentSchedule.module.css';

const { Money } = sdkTypes;

/**
 * Calculates installment prices based on the total payin amount.
 */
const getInstallmentPrices = totalMoney => {
  if (!totalMoney) return null;

  const { amount, currency } = totalMoney;
  const depositAmount = Math.round(amount * 0.3);
  const secondInstallmentAmount = Math.round(amount * 0.4);
  const thirdInstallmentAmount = amount - depositAmount - secondInstallmentAmount;

  return {
    deposit: new Money(depositAmount, currency),
    second: new Money(secondInstallmentAmount, currency),
    third: new Money(thirdInstallmentAmount, currency),
    total: totalMoney,
  };
};

const StatusIcon = ({ paid }) => (
  <img
    className={css.statusIcon}
    src={paid ? '/static/img/icons/check-paid.svg' : '/static/img/icons/check-pending.svg'}
    alt=""
    aria-hidden="true"
  />
);

/**
 * Renders the installment payment schedule with statuses.
 */
const InstallmentSchedule = props => {
  const { price, lineItems, payinTotal, metadata = {}, intl, className } = props;

  let installments = null;

  const secondInstallmentAmount =
    metadata?.installments?.secondInstallmentAmount ||
    metadata?.secondInstallmentAmount ||
    metadata?.installments?.secondInstallmentPrice?.amount ||
    metadata?.secondInstallmentPrice?.amount;
  const thirdInstallmentAmount =
    metadata?.installments?.thirdInstallmentAmount ||
    metadata?.thirdInstallmentAmount ||
    metadata?.installments?.thirdInstallmentPrice?.amount ||
    metadata?.thirdInstallmentPrice?.amount;

  if (payinTotal && payinTotal.amount > 0) {
    const deposit = payinTotal;
    const secondAmt =
      secondInstallmentAmount != null
        ? secondInstallmentAmount
        : Math.round((payinTotal.amount / 3) * 4);
    const thirdAmt =
      thirdInstallmentAmount != null
        ? thirdInstallmentAmount
        : payinTotal.amount;

    const second = new Money(secondAmt, payinTotal.currency);
    const third = new Money(thirdAmt, payinTotal.currency);
    const total = new Money(deposit.amount + second.amount + third.amount, payinTotal.currency);
    installments = {
      deposit,
      second,
      third,
      total,
    };
  } else {
    let totalMoney = payinTotal;

    if (!totalMoney && lineItems && lineItems.length > 0) {
      const customerLineItems = lineItems.filter(item => item.includeFor.includes('customer'));
      const payinTotalAmount = customerLineItems.reduce(
        (sum, item) => sum + item.lineTotal.amount,
        0
      );
      if (payinTotalAmount > 0) {
        const totalAmount = Math.round((payinTotalAmount / 3) * 10);
        const currency = customerLineItems[0].lineTotal.currency;
        totalMoney = new Money(totalAmount, currency);
      }
    }

    if (!totalMoney && price) {
      totalMoney = price;
    }

    installments = getInstallmentPrices(totalMoney);
  }

  if (!installments) return null;

  const depositStatus = payinTotal
    ? metadata?.installments?.firstInstallmentStatus || metadata?.firstInstallmentStatus || 'paid'
    : 'pending';
  const secondStatus =
    metadata?.secondInstallmentStatus === 'paid' ||
    metadata?.installments?.secondInstallmentStatus === 'paid'
      ? 'paid'
      : metadata?.installments?.secondInstallmentStatus ||
        metadata?.secondInstallmentStatus ||
        'pending';
  const thirdStatus =
    metadata?.thirdInstallmentStatus === 'paid' ||
    metadata?.installments?.thirdInstallmentStatus === 'paid'
      ? 'paid'
      : metadata?.installments?.thirdInstallmentStatus ||
        metadata?.thirdInstallmentStatus ||
        'pending';

  const formattedDeposit = formatMoney(intl, installments.deposit);
  const formattedSecond = formatMoney(intl, installments.second);
  const formattedThird = formatMoney(intl, installments.third);

  let paymentMadeAmount = 0;
  if (depositStatus === 'paid') paymentMadeAmount += installments.deposit.amount;
  if (secondStatus === 'paid') paymentMadeAmount += installments.second.amount;
  if (thirdStatus === 'paid') paymentMadeAmount += installments.third.amount;
  const paymentMade = new Money(paymentMadeAmount, installments.total.currency);
  const formattedPaymentMade = formatMoney(intl, paymentMade);

  const rows = [
    {
      key: 'deposit',
      label: intl.formatMessage({ id: 'InstallmentSchedule.depositLabel' }),
      status: depositStatus,
      value: formattedDeposit,
    },
    {
      key: 'second',
      label: intl.formatMessage({ id: 'InstallmentSchedule.secondInstallmentLabel' }),
      status: secondStatus,
      value: formattedSecond,
    },
    {
      key: 'third',
      label: intl.formatMessage({ id: 'InstallmentSchedule.thirdInstallmentLabel' }),
      status: thirdStatus,
      value: formattedThird,
    },
  ];

  return (
    <div className={classNames(css.container, className)}>
      <div className={css.rows}>
        {rows.map(row => {
          const isPaid = row.status === 'paid';
          return (
            <div
              key={row.key}
              className={classNames(css.row, { [css.rowPending]: !isPaid })}
            >
              <div className={css.labelCol}>
                <StatusIcon paid={isPaid} />
                <span className={css.label}>{row.label}</span>
                <span
                  className={classNames(
                    css.statusPill,
                    isPaid ? css.statusPaid : css.statusPending
                  )}
                >
                  {isPaid
                    ? intl.formatMessage({ id: 'InstallmentSchedule.paid' })
                    : intl.formatMessage({ id: 'InstallmentSchedule.pending' })}
                </span>
              </div>
              <span className={css.value}>{row.value}</span>
            </div>
          );
        })}
      </div>

      <div className={css.totalRow}>
        <span className={css.totalLabel}>{intl.formatMessage({ id: 'InstallmentSchedule.paymentMade' })}</span>
        <span className={css.totalValue}>{formattedPaymentMade}</span>
      </div>
    </div>
  );
};

InstallmentSchedule.defaultProps = {
  price: null,
  lineItems: null,
  payinTotal: null,
  metadata: {},
  className: null,
};

InstallmentSchedule.propTypes = {
  price: propTypes.money,
  lineItems: PropTypes.arrayOf(propTypes.transactionLineItem),
  payinTotal: propTypes.money,
  metadata: PropTypes.object,
  intl: PropTypes.object.isRequired,
  className: PropTypes.string,
};

export default InstallmentSchedule;
